import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { reviews } from '../data/reviews';
import { sports } from '../data/sports';
import type { CitySlug, SportSlug } from '../types/landing';
import { ContentNote } from './ContentNote';
import { SectionHeading } from './SectionHeading';
interface ReviewsProps {
  citySlug: CitySlug;
  sportSlug?: SportSlug;
  showNote?: boolean;
}
export function Reviews({
  citySlug,
  sportSlug,
  showNote = true
}: ReviewsProps) {
  const list = useMemo(() => {
    const local = reviews.filter(r => r.city === citySlug);
    if (!sportSlug) return local;
    return [...local.filter(r => r.sport === sportSlug), ...local.filter(r => r.sport !== sportSlug)];
  }, [citySlug, sportSlug]);
  const [start, setStart] = useState(0);
  useEffect(() => setStart(0), [citySlug, sportSlug]);
  const visible = [0, 1, 2].map(o => list[(start + o) % list.length]).filter(Boolean);
  const step = (d: number) => setStart(s => (s + d + list.length) % list.length);
  return <section aria-labelledby="reviews-heading" className="relative bg-brand-ocean px-5 py-12 md:px-10 md:py-14" data-id="element-154" data-mp="2mdknc2xgiwj2">
      <SectionHeading id="reviews-heading" tone="white" data-id="element-155" data-mp="2hdy5thaliu4g">What do parents love about i9 Sports?</SectionHeading>
      <ul className="mt-8 grid gap-6 md:grid-cols-3" data-id="element-156" data-mp="2ddo3qfpxcnuv">
        {visible.map((r, i) => {
        const sportName = sports.find(s => s.slug === r.sport)?.name ?? '';
        return <li key={`${r.id}-${i}`} className={`${i > 0 ? 'hidden md:flex' : 'flex'} flex-col bg-white shadow-md`} data-id="element-157" data-mp="353k7cbefxlfm" data-mpi={i}>
              <div className="flex justify-center gap-1 pt-4" aria-label={`${r.rating} out of 5 stars`} data-id="element-158" data-mp="30caihd0pum4s">
                {[1, 2, 3, 4, 5].map(n => <StarIcon key={n} className={`h-6 w-6 ${n <= r.rating ? 'fill-[#F5B800] text-[#F5B800]' : 'fill-brand-line text-brand-line'}`} aria-hidden="true" data-id="element-159" data-mp="3stmucd3i017d" />)}
              </div>
              <svg viewBox="0 0 300 26" preserveAspectRatio="none" className="mt-3 h-6 w-full" aria-hidden="true" data-id="element-160" data-mp="1m5e32rn2k3ba">
                <polygon points="0,6 300,0 300,10 0,16" fill={i % 2 ? '#1C2369' : '#E8312F'} data-id="element-161" data-mp="1r50hh4emvplo" />
                <polygon points="0,16 300,10 300,26 0,26" fill={i % 2 ? '#E8312F' : '#1C2369'} opacity="0.12" data-id="element-162" data-mp="1cfui9gfspzds" />
              </svg>
              <div className="flex flex-1 flex-col px-5 pb-5 pt-2" data-id="element-163" data-mp="1hdmegmnh47ns">
                <p className="text-xs font-semibold uppercase text-brand-navy" data-id="element-164" data-mp="13f68wz1ckn78">{r.name} – {r.location}</p>
                <p className="text-xs text-brand-muted" data-id="element-165" data-mp="18eiu7fcklm7p">{r.date} · {sportName} parent</p>
                <blockquote className="mt-3 text-sm leading-relaxed text-brand-ink" data-id="element-166" data-mp="tgyschaz075r">“{r.quote}”</blockquote>
              </div>
            </li>;
      })}
      </ul>
      <div className="mt-8 flex justify-center" data-id="element-167" data-mp="yhmthvswy4dp">
        <button type="button" onClick={() => step(-1)} aria-label="Previous reviews" className="flex h-8 w-14 items-center justify-center bg-gray-400 text-white transition-colors duration-150 hover:bg-gray-500" data-id="element-168" data-mp="2nvsz46h7tppk">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-169" data-mp="h795j3yn8muc" />
        </button>
        <button type="button" onClick={() => step(1)} aria-label="Next reviews" className="flex h-8 w-14 items-center justify-center bg-brand-red text-white transition-colors duration-150 hover:bg-brand-redDark" data-id="element-170" data-mp="c7wiai3v1dr8">
          <ChevronRightIcon className="h-4 w-4" data-id="element-171" data-mp="m5akbcp1e000" />
        </button>
      </div>
      {showNote && <ContentNote kind="dynamic" detail="Metro review feed. Sport-tagged reviews sort first." data-id="element-172" data-mp="zwfo8qzfteg5" />}
    </section>;
}